import React, { useState } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LoginPage } from "@/components/LoginPage";
import { Header } from "@/components/Header";
import { EftposScreen } from "@/components/EftposScreen";
import { NewOrderForm } from "@/components/NewOrderForm";
import { SpokeRunTimes } from "@/components/SpokeRunTimes";
import { AdminConsole } from "@/components/AdminConsole";
import { PriceBook } from "@/components/PriceBook";
import { Invoices } from "@/components/Invoices";
import { FranchiseSettings } from "@/components/FranchiseSettings";
import { FranchiseAdmin } from "@/components/FranchiseAdmin";

const Shell = () => {
  const [tab, setTab] = useState("new-order");
  const [refreshSignal, setRefreshSignal] = useState(0);

  const handleCreated = () => setRefreshSignal((n) => n + 1);

  return (
    <div className="min-h-screen bg-dotr-bg text-dotr-ink" data-testid="app-root">
      <Header activeTab={tab} onTabChange={(t) => setTab(t)} />

      <main className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-8 md:py-12">
        {tab === "eftpos" && (
          <div data-testid="page-eftpos">
            <EftposScreen key={refreshSignal} />
          </div>
        )}
        {tab === "new-order" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8" data-testid="page-new-order">
            <div className="lg:col-span-8">
              <NewOrderForm onCreated={handleCreated} />
            </div>
            <div className="lg:col-span-4">
              <SpokeRunTimes />
            </div>
          </div>
        )}
        {tab === "admin" && (
          <div data-testid="page-admin">
            <AdminConsole refreshSignal={refreshSignal} />
          </div>
        )}
        {tab === "pricebook" && <PriceBook />}
        {tab === "invoices" && <Invoices />}
        {tab === "franchise-admin" && <FranchiseAdmin />}
        {tab === "franchise" && <FranchiseSettings />}
      </main>

      <footer className="border-t-2 border-dotr-ink mt-12 py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 flex items-center justify-between flex-wrap gap-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted">
            DOTR · Internal Operations · v1.0
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted">
            Aylesbury · Whitehouse · Bletchley · Glebefarm · Hatfield · Uxbridge
          </div>
        </div>
      </footer>
    </div>
  );
};

const Gate = () => {
  const { status } = useAuth();

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-dotr-bg text-dotr-ink flex items-center justify-center" data-testid="auth-loading">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="animate-spin" size={28} />
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted">
            Verifying session…
          </div>
        </div>
      </div>
    );
  }

  if (status === "guest") {
    return <LoginPage />;
  }

  return <Shell />;
};

function App() {
  return (
    <AuthProvider>
      <Gate />
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            border: "2px solid #1A1714",
            borderRadius: 0,
            background: "#FFFFFF",
            color: "#1A1714",
            boxShadow: "4px 4px 0 #1A1714",
            fontFamily: "'Chivo', sans-serif",
          },
        }}
      />
    </AuthProvider>
  );
}

export default App;
